/** Attendance & Leave — clock, month grid, regularization, leaves, WFH, approvals, HR settings. */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtWorkspaceTime } from "@/lib/wsTime";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CalendarDays, Plane, Home, ClipboardCheck, Settings2, Plus, Trash2, CheckCircle2, XCircle, BarChart3, TrendingUp, Pencil, ShieldCheck, BookOpen, Wallet, Fingerprint, Upload, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import AttendanceInsightsTab from "@/components/attendance/AttendanceInsightsTab";
import AttendanceComplianceRulesEditor from "@/components/attendance/AttendanceComplianceRulesEditor";
import HrAdminRecordsTab from "@/components/attendance/HrAdminRecordsTab";
import LeaveLedgerTab from "@/components/attendance/LeaveLedgerTab";
import WorkLocationsSettings from "@/components/attendance/WorkLocationsSettings";
import BiometricUploadTab from "@/components/attendance/BiometricUploadTab";
import AdvancedTable from "@/components/table/AdvancedTable";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const STATUS_COLORS = { present: "bg-emerald-500", half_day: "bg-amber-500", short: "bg-orange-500", leave: "bg-sky-500", half_day_leave: "bg-sky-400", wfh: "bg-violet-500", holiday: "bg-pink-400", weekly_off: "bg-foreground/20" };

export default function Attendance() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can, ready } = useWorkspacePerms();
  const canManage = !ready || can("hr.manage");
  const canViewInsights = !ready || can("hr.manage") || can("hr.approve") || can("timesheet.report");
  const canHrAdmin = !ready || can("hr.manage") || can("hr.approve");
  // Feb 2026 · Route-driven mode. Personal apply/view lives at /app/my-attendance
  // (under My Dashboard) — the HR page at /app/attendance shows admin-only tabs.
  const { pathname } = useLocation();
  const isPersonal = pathname.startsWith("/app/my-attendance");

  return (
    <>
      <PageHeader
        eyebrow={isPersonal ? "ME" : "HRMS"}
        title={isPersonal ? "My attendance & leave" : "Attendance & Leave · HR"}
        description={isPersonal
          ? "Clock in/out, regularize, apply for your own leave or WFH, and track your balances."
          : "Manage the whole team's attendance, leaves, WFH, adjustments, ledgers and rules."}
        testid="attendance-header" />
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        <Tabs defaultValue={isPersonal ? "month" : (canHrAdmin ? "hr-admin" : "insights")}>
          <TabsList className="rounded-none flex-wrap h-auto">
            {isPersonal && <TabsTrigger value="month" className="rounded-none" data-testid="att-tab-month"><CalendarDays className="w-4 h-4 mr-2" />My attendance</TabsTrigger>}
            {isPersonal && <TabsTrigger value="leaves" className="rounded-none" data-testid="att-tab-leaves"><Plane className="w-4 h-4 mr-2" />My leaves</TabsTrigger>}
            {isPersonal && <TabsTrigger value="wfh" className="rounded-none" data-testid="att-tab-wfh"><Home className="w-4 h-4 mr-2" />My WFH</TabsTrigger>}
            {isPersonal && <TabsTrigger value="approvals" className="rounded-none" data-testid="att-tab-approvals"><ClipboardCheck className="w-4 h-4 mr-2" />Team approvals</TabsTrigger>}
            {!isPersonal && canHrAdmin && <TabsTrigger value="hr-admin" className="rounded-none" data-testid="att-tab-hr-admin"><ShieldCheck className="w-4 h-4 mr-2" />HR records</TabsTrigger>}
            {!isPersonal && canHrAdmin && <TabsTrigger value="imports" className="rounded-none" data-testid="att-tab-imports"><Upload className="w-4 h-4 mr-2" />Bulk imports</TabsTrigger>}
            {!isPersonal && canHrAdmin && <TabsTrigger value="ledgers" className="rounded-none" data-testid="att-tab-ledgers"><BookOpen className="w-4 h-4 mr-2" />Leave ledgers</TabsTrigger>}
            {!isPersonal && canHrAdmin && <TabsTrigger value="biometric" className="rounded-none" data-testid="att-tab-biometric"><Fingerprint className="w-4 h-4 mr-2" />Biometric device</TabsTrigger>}
            {!isPersonal && canViewInsights && <TabsTrigger value="insights" className="rounded-none" data-testid="att-tab-insights"><BarChart3 className="w-4 h-4 mr-2" />Insights</TabsTrigger>}
            {!isPersonal && canManage && <TabsTrigger value="settings" className="rounded-none" data-testid="att-tab-settings"><Settings2 className="w-4 h-4 mr-2" />HR settings</TabsTrigger>}
          </TabsList>
          {isPersonal && <TabsContent value="month" className="mt-5"><MonthTab wsId={wsId} /></TabsContent>}
          {isPersonal && <TabsContent value="leaves" className="mt-5"><LeavesTab wsId={wsId} /></TabsContent>}
          {isPersonal && <TabsContent value="wfh" className="mt-5"><WfhTab wsId={wsId} /></TabsContent>}
          {isPersonal && <TabsContent value="approvals" className="mt-5"><ApprovalsTab wsId={wsId} /></TabsContent>}
          {!isPersonal && canHrAdmin && <TabsContent value="hr-admin" className="mt-5"><HrAdminRecordsTab wsId={wsId} /></TabsContent>}
          {!isPersonal && canHrAdmin && <TabsContent value="imports" className="mt-5"><HrImportsTab wsId={wsId} /></TabsContent>}
          {!isPersonal && canHrAdmin && <TabsContent value="ledgers" className="mt-5"><LeaveLedgerTab wsId={wsId} /></TabsContent>}
          {!isPersonal && canHrAdmin && <TabsContent value="biometric" className="mt-5"><BiometricUploadTab wsId={wsId} /></TabsContent>}
          {!isPersonal && canViewInsights && <TabsContent value="insights" className="mt-5"><AttendanceInsightsTab wsId={wsId} /></TabsContent>}
          {!isPersonal && canManage && <TabsContent value="settings" className="mt-5"><SettingsTab wsId={wsId} /></TabsContent>}
        </Tabs>
      </div>
    </>
  );
}

export function MonthTab({ wsId }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [reg, setReg] = useState({ date: "", requested_in: "09:00", requested_out: "18:00", reason: "" });
  const load = () => api.get(`/workspaces/${wsId}/attendance?month=${month}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (wsId) load(); }, [wsId, month]); // eslint-disable-line
  const requestReg = async () => {
    if (!reg.date || !reg.reason) { toast.error("Date and reason required"); return; }
    try { await api.post(`/workspaces/${wsId}/attendance/regularize`, reg); toast.success("Regularization requested"); setReg({ ...reg, date: "", reason: "" }); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  if (!data) return <div className="text-sm text-foreground/55">Loading…</div>;
  const byDate = Object.fromEntries((data.days || []).map((d) => [d.date, d]));
  const holByDate = Object.fromEntries((data.holidays || []).map((h) => [h.date, h]));
  const [y, m] = month.split("-").map(Number);
  const nDays = new Date(y, m, 0).getDate();
  return (
    <div className="space-y-4" data-testid="attendance-month">
      <div className="flex items-center gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-8 w-44 rounded-none text-xs" data-testid="att-month-picker" />
        <span className="text-[11px] text-foreground/55">Shift: {data.shift?.name} ({data.shift?.start_time}–{data.shift?.end_time})</span>
      </div>
      <div className="grid grid-cols-7 gap-1" data-testid="att-grid">
        {Array.from({ length: nDays }, (_, i) => {
          const d = `${month}-${String(i + 1).padStart(2, "0")}`;
          const rec = byDate[d]; const hol = holByDate[d];
          const wd = new Date(d + "T00:00:00Z").getUTCDay(); // 0=Sun
          const off = (data.shift?.weekly_offs || []).includes((wd + 6) % 7);
          const status = rec?.status || (hol ? "holiday" : off ? "weekly_off" : null);
          return (
            <div key={d} className="border border-border p-1.5 min-h-16 text-[10px]" data-testid={`att-day-${d}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono">{i + 1}</span>
                {status && <span className={`w-2 h-2 rounded-full ${STATUS_COLORS[status] || "bg-foreground/30"}`} title={status} />}
              </div>
              {rec?.clock_in && <div className="mt-1 font-mono">{fmtWorkspaceTime(rec.clock_in)}–{rec.clock_out ? fmtWorkspaceTime(rec.clock_out) : "…"}</div>}
              {rec?.late_minutes > 0 && <div className="text-red-500">late {rec.late_minutes}m</div>}
              {hol && <div className="text-pink-500 truncate">{hol.name}</div>}
              {status === "leave" && <div className="text-sky-500">leave</div>}
            </div>
          );
        })}
      </div>
      <div className="border border-border p-3 space-y-2" data-testid="regularize-panel">
        <div className="label-eyebrow text-[10px]">Request regularization (missed / wrong punch)</div>
        <div className="flex flex-wrap gap-2">
          <Input type="date" value={reg.date} onChange={(e) => setReg({ ...reg, date: e.target.value })} className="h-8 w-40 rounded-none text-xs" data-testid="reg-date" />
          <Input type="time" value={reg.requested_in} onChange={(e) => setReg({ ...reg, requested_in: e.target.value })} className="h-8 w-28 rounded-none text-xs" data-testid="reg-in" />
          <Input type="time" value={reg.requested_out} onChange={(e) => setReg({ ...reg, requested_out: e.target.value })} className="h-8 w-28 rounded-none text-xs" data-testid="reg-out" />
          <Input value={reg.reason} onChange={(e) => setReg({ ...reg, reason: e.target.value })} placeholder="Reason" className="h-8 flex-1 min-w-40 rounded-none text-xs" data-testid="reg-reason" />
          <Button size="sm" className="rounded-none h-8" onClick={requestReg} data-testid="reg-submit">Request</Button>
        </div>
      </div>
    </div>
  );
}

export function LeavesTab({ wsId }) {
  const [balances, setBalances] = useState([]);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({ type_id: "", from_date: "", to_date: "", half_day: false, reason: "" });
  const [selIds, setSelIds] = useState(new Set());
  const [editing, setEditing] = useState(null); // full leave row being edited
  const load = () => Promise.all([
    api.get(`/workspaces/${wsId}/leaves/balances`).then((r) => setBalances(r.data)),
    api.get(`/workspaces/${wsId}/leaves?mine=true`).then((r) => setMine(r.data)),
  ]).catch(() => {});
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line
  const apply = async () => {
    try {
      await api.post(`/workspaces/${wsId}/leaves`, { ...form, to_date: form.half_day ? form.from_date : form.to_date });
      toast.success("Leave applied — pending approval"); setForm({ type_id: "", from_date: "", to_date: "", half_day: false, reason: "" }); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const cancel = async (id) => {
    try { await api.post(`/leaves/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} leave request${ids.length === 1 ? "" : "s"} permanently?`)) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/leaves/bulk-delete`, { ids });
      toast.success(`Deleted ${data.deleted}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setSelIds(new Set()); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk delete failed")); }
  };
  const columns = [
    { key: "type_code", label: "Type", width: 90, sortable: true, groupable: true, filterType: "text",
      render: (l) => <Badge variant="outline" className="rounded-none text-[9px]">{l.type_code}</Badge> },
    { key: "from_date", label: "From", width: 110, sortable: true, filterType: "date-range" },
    { key: "to_date", label: "To", width: 110, sortable: true, filterType: "date-range" },
    { key: "days", label: "Days", width: 80, sortable: true, align: "right", sum: true, type: "number",
      render: (l) => <span className="font-mono font-bold">{l.days}{l.half_day ? " (½)" : ""}</span> },
    { key: "reason", label: "Reason", width: 260, filterType: "text",
      render: (l) => <span className="text-xs text-foreground/70 truncate">{l.reason || ""}</span> },
    { key: "status", label: "Status", width: 110, sortable: true, groupable: true, filterType: "select",
      filterOptions: [{ value: "pending", label: "pending" }, { value: "approved", label: "approved" }, { value: "rejected", label: "rejected" }, { value: "cancelled", label: "cancelled" }],
      render: (l) => <Badge variant="outline" className={`rounded-none text-[9px] ${l.status === "approved" ? "text-emerald-600" : l.status === "rejected" ? "text-red-500" : ""}`}>{l.status}</Badge> },
    { key: "created_at", label: "Applied", width: 130, sortable: true, filterType: "date-range",
      render: (l) => <span className="text-[10px] text-muted-foreground">{(l.created_at || "").slice(0, 10)}</span> },
  ];
  return (
    <div className="space-y-4" data-testid="leaves-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="leave-balances">
        {balances.map((b) => (
          <div key={b.type_id} className="border border-border p-3" style={{ borderTopColor: b.color, borderTopWidth: 3 }}>
            <div className="text-[10px] label-eyebrow">{b.name}</div>
            <div className="text-xl font-black font-mono" data-testid={`balance-${b.code}`}>{b.available}</div>
            <div className="text-[10px] text-foreground/55">of {b.accrued} accrued · {b.used} used{b.pending ? ` · ${b.pending} pending` : ""}</div>
          </div>
        ))}
      </div>
      <div className="border border-border p-3 flex flex-wrap gap-2 items-center" data-testid="leave-apply">
        <Select value={form.type_id} onValueChange={(v) => setForm({ ...form, type_id: v })}>
          <SelectTrigger className="h-8 w-44 rounded-none text-xs" data-testid="leave-type-select"><SelectValue placeholder="Leave type…" /></SelectTrigger>
          <SelectContent>{balances.map((b) => <SelectItem key={b.type_id} value={b.type_id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="h-8 w-40 rounded-none text-xs" data-testid="leave-from" />
        <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} disabled={form.half_day} className="h-8 w-40 rounded-none text-xs" data-testid="leave-to" />
        <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} data-testid="leave-half" /> Half day</label>
        <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason" className="h-8 flex-1 min-w-40 rounded-none text-xs" data-testid="leave-reason" />
        <Button size="sm" className="rounded-none h-8" onClick={apply} data-testid="leave-apply-btn"><Plus className="w-3.5 h-3.5 mr-1" />Apply</Button>
      </div>
      <AdvancedTable
        tableKey="my-leaves"
        title="My leave requests"
        rows={mine}
        columns={columns}
        loading={false}
        emptyIcon={Plane}
        emptyTitle="No leave requests yet"
        emptyDescription="Apply above to submit your first request."
        selectable
        selectedIds={selIds}
        onSelectionChange={setSelIds}
        renderBulkBar={() => (
          <Button size="sm" variant="outline" className="rounded-none h-7 text-red-600 border-red-500/50" onClick={bulkDelete} data-testid="leaves-bulk-delete">
            <Trash2 className="w-3.5 h-3.5 mr-1" />Delete {selIds.size}
          </Button>
        )}
        renderRowActions={(l) => (
          <div className="flex items-center gap-1">
            {l.status === "pending" && (
              <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px]" onClick={(e) => { e.stopPropagation(); setEditing(l); }} data-testid={`leave-edit-${l.id}`}>
                <Pencil className="w-3 h-3 mr-0.5" />Edit
              </Button>
            )}
            {["pending", "approved"].includes(l.status) && (
              <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px] text-red-500" onClick={(e) => { e.stopPropagation(); cancel(l.id); }} data-testid={`leave-cancel-${l.id}`}>Cancel</Button>
            )}
          </div>
        )}
      />
      <LeaveEditDialog
        wsId={wsId}
        row={editing}
        types={balances}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function LeaveEditDialog({ wsId, row, types, onClose, onSaved }) {
  const [form, setForm] = useState({ type_id: "", from_date: "", to_date: "", half_day: false, reason: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (row) {
      setForm({
        type_id: row.type_id || "",
        from_date: row.from_date || "",
        to_date: row.to_date || "",
        half_day: !!row.half_day,
        reason: row.reason || "",
      });
    }
  }, [row]);
  const save = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await api.patch(`/leaves/${row.id}`, {
        type_id: form.type_id,
        from_date: form.from_date,
        to_date: form.half_day ? form.from_date : form.to_date,
        half_day: form.half_day,
        reason: form.reason,
      });
      toast.success("Leave updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="leave-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit leave request</DialogTitle>
          <DialogDescription>Owners can edit pending requests. HR admins can edit any status.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Leave type</Label>
            <Select value={form.type_id} onValueChange={(v) => setForm({ ...form, type_id: v })}>
              <SelectTrigger data-testid="leave-edit-type"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>From</Label><Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="leave-edit-from" /></div>
            <div><Label>To</Label><Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} disabled={form.half_day} data-testid="leave-edit-to" /></div>
          </div>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked })} data-testid="leave-edit-half" /> Half day</label>
          <div><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-edit-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.type_id || !form.from_date} data-testid="leave-edit-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WfhTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ date: "", reason: "" });
  const [selIds, setSelIds] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/workspaces/${wsId}/wfh?mine=true`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line
  const apply = async () => {
    try { await api.post(`/workspaces/${wsId}/wfh`, form); toast.success("WFH requested"); setForm({ date: "", reason: "" }); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} WFH request${ids.length === 1 ? "" : "s"} permanently?`)) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/wfh/bulk-delete`, { ids });
      toast.success(`Deleted ${data.deleted}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setSelIds(new Set()); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk delete failed")); }
  };
  const columns = [
    { key: "date", label: "Date", width: 120, sortable: true, filterType: "date-range",
      render: (w) => <span className="font-mono">{w.date}</span> },
    { key: "week", label: "Week", width: 90, sortable: true,
      render: (w) => <span className="text-foreground/50 text-[10px]">{w.week}</span> },
    { key: "reason", label: "Reason", width: 280, filterType: "text",
      render: (w) => <span className="text-xs text-foreground/70 truncate">{w.reason || ""}</span> },
    { key: "status", label: "Status", width: 110, sortable: true, groupable: true, filterType: "select",
      filterOptions: [{ value: "pending", label: "pending" }, { value: "approved", label: "approved" }, { value: "rejected", label: "rejected" }, { value: "cancelled", label: "cancelled" }],
      render: (w) => <Badge variant="outline" className={`rounded-none text-[9px] ${w.status === "approved" ? "text-emerald-600" : w.status === "rejected" ? "text-red-500" : ""}`}>{w.status}</Badge> },
  ];
  return (
    <div className="space-y-3" data-testid="wfh-tab">
      <div className="border border-border p-3 flex flex-wrap gap-2">
        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-8 w-40 rounded-none text-xs" data-testid="wfh-date" />
        <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason (optional)" className="h-8 flex-1 min-w-40 rounded-none text-xs" data-testid="wfh-reason" />
        <Button size="sm" className="rounded-none h-8" onClick={apply} data-testid="wfh-apply-btn"><Home className="w-3.5 h-3.5 mr-1" />Request WFH</Button>
      </div>
      <AdvancedTable
        tableKey="my-wfh"
        title="My WFH requests"
        rows={rows}
        columns={columns}
        loading={false}
        emptyIcon={Home}
        emptyTitle="No WFH requests yet"
        emptyDescription="Request WFH above to submit your first."
        selectable
        selectedIds={selIds}
        onSelectionChange={setSelIds}
        renderBulkBar={() => (
          <Button size="sm" variant="outline" className="rounded-none h-7 text-red-600 border-red-500/50" onClick={bulkDelete} data-testid="wfh-bulk-delete">
            <Trash2 className="w-3.5 h-3.5 mr-1" />Delete {selIds.size}
          </Button>
        )}
        renderRowActions={(w) => w.status === "pending" ? (
          <Button size="sm" variant="ghost" className="rounded-none h-6 text-[10px]" onClick={(e) => { e.stopPropagation(); setEditing(w); }} data-testid={`wfh-edit-${w.id}`}>
            <Pencil className="w-3 h-3 mr-0.5" />Edit
          </Button>
        ) : null}
      />
      <WfhEditDialog
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function WfhEditDialog({ row, onClose, onSaved }) {
  const [form, setForm] = useState({ date: "", reason: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (row) setForm({ date: row.date || "", reason: row.reason || "" });
  }, [row]);
  const save = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await api.patch(`/wfh/${row.id}`, { date: form.date, reason: form.reason });
      toast.success("WFH updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="wfh-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit WFH request</DialogTitle>
          <DialogDescription>Owners can edit pending requests. HR admins can edit any status.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="wfh-edit-date" /></div>
          <div><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="wfh-edit-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.date} data-testid="wfh-edit-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalsTab({ wsId }) {
  const [leaves, setLeaves] = useState([]);
  const [wfh, setWfh] = useState([]);
  const [regs, setRegs] = useState([]);
  const [leaveSel, setLeaveSel] = useState(new Set());
  const [wfhSel, setWfhSel] = useState(new Set());
  const [scorecard, setScorecard] = useState(null);
  const load = () => Promise.all([
    api.get(`/workspaces/${wsId}/leaves?mine=false&status=pending`).then((r) => setLeaves(r.data)),
    api.get(`/workspaces/${wsId}/wfh?mine=false&status=pending`).then((r) => setWfh(r.data)),
    api.get(`/workspaces/${wsId}/attendance/regularizations`).then((r) => setRegs(r.data)),
    api.get(`/me/manager-scorecard?ws_id=${wsId}&days=30`).then((r) => setScorecard(r.data)).catch(() => {}),
  ]).catch(() => {});
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line

  const review = async (path, approve) => {
    try { await api.post(path, { approve, reason: approve ? null : window.prompt("Reason?") || "" }); toast.success(approve ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const bulkReview = async (kind, approve) => {
    const sel = kind === "leaves" ? leaveSel : wfhSel;
    const ids = Array.from(sel);
    if (!ids.length) return;
    const reason = approve ? null : (window.prompt("Reason for rejecting all selected?") || "");
    try {
      const { data } = await api.post(`/workspaces/${wsId}/${kind}/bulk-review`, { ids, approve, reason });
      toast.success(`${approve ? "Approved" : "Rejected"} ${data.reviewed}${data.skipped ? ` · skipped ${data.skipped}` : ""}`);
      (kind === "leaves" ? setLeaveSel : setWfhSel)(new Set());
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk review failed")); }
  };

  const leaveColumns = [
    { key: "user_name", label: "Member", width: 200, sortable: true, filterType: "text", frozen: true },
    { key: "type_code", label: "Type", width: 90, sortable: true, groupable: true, filterType: "text",
      render: (l) => <Badge variant="outline" className="rounded-none text-[9px]">{l.type_code}</Badge> },
    { key: "from_date", label: "From", width: 110, sortable: true, filterType: "date-range" },
    { key: "to_date", label: "To", width: 110, sortable: true, filterType: "date-range" },
    { key: "days", label: "Days", width: 70, sortable: true, align: "right", sum: true, type: "number",
      render: (l) => <span className="font-mono font-bold">{l.days}</span> },
    { key: "reason", label: "Reason", width: 260, filterType: "text",
      render: (l) => <span className="text-xs text-foreground/70 truncate">{l.reason || ""}</span> },
  ];
  const wfhColumns = [
    { key: "user_name", label: "Member", width: 200, sortable: true, filterType: "text", frozen: true },
    { key: "date", label: "Date", width: 120, sortable: true, filterType: "date-range" },
    { key: "week", label: "Week", width: 80, sortable: true },
    { key: "reason", label: "Reason", width: 300, filterType: "text",
      render: (w) => <span className="text-xs text-foreground/70 truncate">{w.reason || ""}</span> },
  ];

  const empty = leaves.length + wfh.length + regs.length === 0;
  const approveRejectActions = (id, path) => (
    <span className="flex gap-1 justify-end">
      <Button size="sm" variant="ghost" className="rounded-none h-6 px-1 text-emerald-600" onClick={(e) => { e.stopPropagation(); review(path, true); }} data-testid={`appr-${id}-approve`}>
        <CheckCircle2 className="w-3 h-3" />
      </Button>
      <Button size="sm" variant="ghost" className="rounded-none h-6 px-1 text-red-600" onClick={(e) => { e.stopPropagation(); review(path, false); }} data-testid={`appr-${id}-reject`}>
        <XCircle className="w-3 h-3" />
      </Button>
    </span>
  );

  return (
    <div className="space-y-4" data-testid="hr-approvals">
      {scorecard && (scorecard.processed > 0 || scorecard.pending > 0) && (
        <div className="border border-primary/30 bg-primary/5 p-4" data-testid="manager-scorecard">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <div className="label-eyebrow text-[10px] text-primary">Your manager scorecard · last {scorecard.days} days</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-2">
            <ScorecardStat label="Approved" value={scorecard.approved} color="text-emerald-600" testid="scorecard-approved" />
            <ScorecardStat label="Rejected" value={scorecard.rejected} color="text-red-600" testid="scorecard-rejected" />
            <ScorecardStat label="Avg response" value={humanizeHours(scorecard.avg_response_hours)} testid="scorecard-avg" />
            <ScorecardStat label="Pending" value={scorecard.pending} color={scorecard.pending > 0 ? "text-amber-600" : ""} testid="scorecard-pending" />
            <ScorecardStat label="Processed" value={scorecard.processed} testid="scorecard-processed" />
          </div>
          <p className="text-xs text-foreground/70 mt-1" data-testid="scorecard-narrative">{scorecard.narrative}</p>
        </div>
      )}
      {empty && <div className="text-sm text-foreground/55 border border-border p-6 text-center" data-testid="hr-approvals-empty">Nothing pending on you.</div>}
      {leaves.length > 0 && (
        <AdvancedTable
          tableKey="hr-appr-leaves"
          title="Leave requests"
          rows={leaves}
          columns={leaveColumns}
          loading={false}
          emptyIcon={Plane}
          emptyTitle="No pending leave requests"
          emptyDescription="Everything's approved / rejected."
          selectable
          selectedIds={leaveSel}
          onSelectionChange={setLeaveSel}
          renderBulkBar={() => (
            <>
              <Button size="sm" className="rounded-none h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => bulkReview("leaves", true)} data-testid="hr-appr-leaves-bulk-approve">
                <CheckCircle2 className="w-3 h-3 mr-1" />Approve
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-red-600 border-red-500/50" onClick={() => bulkReview("leaves", false)} data-testid="hr-appr-leaves-bulk-reject">
                <XCircle className="w-3 h-3 mr-1" />Reject
              </Button>
            </>
          )}
          renderRowActions={(l) => approveRejectActions(l.id, `/leaves/${l.id}/review`)}
        />
      )}
      {wfh.length > 0 && (
        <AdvancedTable
          tableKey="hr-appr-wfh"
          title="WFH requests"
          rows={wfh}
          columns={wfhColumns}
          loading={false}
          emptyIcon={Home}
          emptyTitle="No pending WFH requests"
          emptyDescription="You're all caught up."
          selectable
          selectedIds={wfhSel}
          onSelectionChange={setWfhSel}
          renderBulkBar={() => (
            <>
              <Button size="sm" className="rounded-none h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => bulkReview("wfh", true)} data-testid="hr-appr-wfh-bulk-approve">
                <CheckCircle2 className="w-3 h-3 mr-1" />Approve
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-red-600 border-red-500/50" onClick={() => bulkReview("wfh", false)} data-testid="hr-appr-wfh-bulk-reject">
                <XCircle className="w-3 h-3 mr-1" />Reject
              </Button>
            </>
          )}
          renderRowActions={(w) => approveRejectActions(w.id, `/wfh/${w.id}/review`)}
        />
      )}
      {regs.length > 0 && (
        <div className="border border-border"><div className="px-3 py-2 label-eyebrow text-[10px] bg-muted/40">Attendance regularizations</div>
          {regs.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border text-xs" data-testid={`appr-reg-${r.id}`}>
              <b>{r.user_name}</b><span className="font-mono">{r.date} {r.requested_in}–{r.requested_out}</span><span className="flex-1 truncate text-foreground/55">{r.reason}</span>
              <Button size="sm" className="rounded-none h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => review(`/attendance-regularizations/${r.id}/review`, true)} data-testid={`appr-reg-${r.id}-approve`}><CheckCircle2 className="w-3 h-3" /></Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-red-600" onClick={() => review(`/attendance-regularizations/${r.id}/review`, false)} data-testid={`appr-reg-${r.id}-reject`}><XCircle className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab({ wsId }) {
  const [st, setSt] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [types, setTypes] = useState([]);
  const year = new Date().getFullYear();
  const [newShift, setNewShift] = useState({ name: "", start_time: "09:00", end_time: "18:00" });
  const [newHol, setNewHol] = useState({ date: "", name: "", kind: "public" });
  const load = () => Promise.all([
    api.get(`/workspaces/${wsId}/hr-settings`).then((r) => setSt(r.data)),
    api.get(`/workspaces/${wsId}/shifts`).then((r) => setShifts(r.data)),
    api.get(`/workspaces/${wsId}/holidays?year=${year}`).then((r) => setHolidays(r.data.holidays)),
    api.get(`/workspaces/${wsId}/leave-types`).then((r) => setTypes(r.data)),
  ]).catch(() => {});
  useEffect(() => { if (wsId) load(); }, [wsId]); // eslint-disable-line
  const saveSettings = async () => { try { await api.put(`/workspaces/${wsId}/hr-settings`, st); toast.success("Settings saved"); } catch (e) { toast.error(apiErrorMessage(e, "Failed")); } };
  if (!st) return <div className="text-sm text-foreground/55">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="hr-settings">
      <AttendanceComplianceRulesEditor wsId={wsId} />
      <div className="border border-border p-3 space-y-2" data-testid="checkin-enforcement-panel">
        <div className="label-eyebrow text-[10px]">Check-in enforcement</div>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={!!st.force_checkin} onChange={(e) => setSt({ ...st, force_checkin: e.target.checked })} className="mt-0.5" data-testid="setting-force-checkin" />
          <span>
            <b>Force check-in.</b>
            <span className="text-xs text-muted-foreground block">If a user hasn&apos;t clocked in for the day, block navigation with a modal until they do.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={!!st.enforce_location_checkin} onChange={(e) => setSt({ ...st, enforce_location_checkin: e.target.checked })} className="mt-0.5" data-testid="setting-enforce-location" />
          <span>
            <b>Enforce location check-in.</b>
            <span className="text-xs text-muted-foreground block">Only allow check-in from an assigned work location (geofence or WiFi SSID match).</span>
          </span>
        </label>
        <Button size="sm" className="rounded-none h-8" onClick={saveSettings} data-testid="settings-save-enforcement">Save enforcement settings</Button>
      </div>
      <WorkLocationsSettings wsId={wsId} />
      <div className="border border-border p-3 space-y-2">
        <div className="label-eyebrow text-[10px]">Policies</div>
        <div className="grid sm:grid-cols-5 gap-2 text-xs">
          {[["floating_quota", "Floating holidays/yr"], ["wfh_weekly_quota", "WFH days/week"], ["min_full_day_hours", "Full day (hrs)"], ["min_half_day_hours", "Half day (hrs)"], ["late_grace_minutes", "Late grace (min)"]].map(([k, label]) => (
            <div key={k}><div className="text-[10px] text-foreground/55 mb-1">{label}</div>
              <Input type="number" value={st[k]} onChange={(e) => setSt({ ...st, [k]: Number(e.target.value) })} className="h-8 rounded-none text-xs font-mono" data-testid={`setting-${k}`} /></div>
          ))}
        </div>
        <Button size="sm" className="rounded-none h-8" onClick={saveSettings} data-testid="settings-save">Save policies</Button>
      </div>
      <div className="border border-border p-3 space-y-2" data-testid="shifts-panel">
        <div className="label-eyebrow text-[10px]">Shifts</div>
        {shifts.map((s) => (
          <div key={s.id} className="flex items-center gap-2 text-xs border border-border px-2 py-1.5">
            <b>{s.name}</b><span className="font-mono">{s.start_time}–{s.end_time}</span>
            {s.is_default && <Badge variant="outline" className="rounded-none text-[9px]">default</Badge>}
            <button className="ml-auto text-red-500" onClick={() => api.delete(`/shifts/${s.id}`).then(load)} data-testid={`shift-del-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Input value={newShift.name} onChange={(e) => setNewShift({ ...newShift, name: e.target.value })} placeholder="Shift name" className="h-8 w-40 rounded-none text-xs" data-testid="shift-name" />
          <Input type="time" value={newShift.start_time} onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })} className="h-8 w-28 rounded-none text-xs" data-testid="shift-start" />
          <Input type="time" value={newShift.end_time} onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })} className="h-8 w-28 rounded-none text-xs" data-testid="shift-end" />
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => api.post(`/workspaces/${wsId}/shifts`, { ...newShift, is_default: shifts.length === 0 }).then(() => { setNewShift({ name: "", start_time: "09:00", end_time: "18:00" }); load(); }).catch((e) => toast.error(apiErrorMessage(e, "Failed")))} data-testid="shift-add"><Plus className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      <div className="border border-border p-3 space-y-2" data-testid="holidays-panel">
        <div className="flex items-center gap-2">
          <div className="label-eyebrow text-[10px]">Holiday calendar {year} (public + floating options)</div>
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="outline"
                    className="rounded-none h-7 text-[10px] uppercase tracking-widest"
                    onClick={async () => {
                      const cc = window.prompt("Country code (2-letter, e.g. IN, US, GB, CA, AU)", "IN");
                      if (!cc) return;
                      try {
                        const r = await api.post(`/workspaces/${wsId}/holidays/import`, {
                          country_code: cc.trim().toUpperCase(), year,
                        });
                        toast.success(`Imported ${r.data?.created || 0} holiday${r.data?.created === 1 ? "" : "s"} (${r.data?.skipped || 0} already existed)`);
                        load();
                      } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
                    }}
                    data-testid="holiday-import-btn"
                    title="Pull government-notified public holidays from Nager.Date">
              Import public holidays
            </Button>
          </div>
        </div>
        {holidays.map((h) => (
          <div key={h.id} className="flex items-center gap-2 text-xs border border-border px-2 py-1.5">
            <span className="font-mono">{h.date}</span><b>{h.name}</b>
            <Badge variant="outline" className={`rounded-none text-[9px] ${h.kind === "floating" ? "text-violet-600" : ""}`}>{h.kind}</Badge>
            <button className="ml-auto text-red-500" onClick={() => api.delete(`/holidays/${h.id}`).then(load)} data-testid={`holiday-del-${h.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Input type="date" value={newHol.date} onChange={(e) => setNewHol({ ...newHol, date: e.target.value })} className="h-8 w-40 rounded-none text-xs" data-testid="holiday-date" />
          <Input value={newHol.name} onChange={(e) => setNewHol({ ...newHol, name: e.target.value })} placeholder="Holiday name" className="h-8 w-44 rounded-none text-xs" data-testid="holiday-name" />
          <Select value={newHol.kind} onValueChange={(v) => setNewHol({ ...newHol, kind: v })}>
            <SelectTrigger className="h-8 w-32 rounded-none text-xs" data-testid="holiday-kind"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="public">Public</SelectItem><SelectItem value="floating">Floating</SelectItem></SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => api.post(`/workspaces/${wsId}/holidays`, newHol).then(() => { setNewHol({ date: "", name: "", kind: "public" }); load(); }).catch((e) => toast.error(apiErrorMessage(e, "Failed")))} data-testid="holiday-add"><Plus className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      <LeaveTypesEditor wsId={wsId} types={types} onChange={load} />
    </div>
  );
}

/**
 * R90 · Full CRUD editor for Leave Types.
 * Standard HR knobs — annual quota, accrual frequency (annual / monthly /
 * quarterly), carry-forward rule (none / capped / unlimited) with cap,
 * min-notice days, requires-approval, paid vs unpaid, colour, and an
 * `enabled` toggle to deactivate without breaking historical requests.
 */
function LeaveTypesEditor({ wsId, types, onChange }) {
  const [editing, setEditing] = useState(null);   // null | "new" | leaveType doc
  const openNew = () => setEditing({
    _new: true,
    code: "", name: "", annual_quota: 12, accrual: "annual",
    carry_forward_rule: "none", carry_forward_max: 0,
    min_notice_days: 0, requires_approval: true, is_paid: true,
    color: "#0ea5e9", enabled: true,
  });
  const toggleEnabled = async (t) => {
    try {
      await api.patch(`/leave-types/${t.id}`, { ...t, enabled: !t.enabled });
      toast.success(t.enabled ? "Leave type deactivated" : "Leave type activated");
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  const delType = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Requests referencing it will block deletion; deactivate instead if unsure.`)) return;
    try {
      await api.delete(`/leave-types/${t.id}`);
      toast.success("Leave type deleted");
      onChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed — deactivate instead if unsure")); }
  };
  return (
    <div className="border border-border p-3 space-y-2" data-testid="leave-types-panel">
      <div className="flex items-center gap-2">
        <div className="label-eyebrow text-[10px] flex-1">Leave types</div>
        <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={openNew} data-testid="leave-type-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> New leave type
        </Button>
      </div>
      <div className="border border-border">
        <div className="grid grid-cols-[24px_1fr_90px_120px_140px_120px_80px_120px] gap-2 px-2 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
          <div></div>
          <div>Name</div>
          <div>Code</div>
          <div>Quota / yr</div>
          <div>Accrual</div>
          <div>Carry-over</div>
          <div>Paid</div>
          <div className="text-right">Actions</div>
        </div>
        {types.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground italic text-center">No leave types yet — click &quot;New leave type&quot; to add one.</div>
        ) : types.map((t) => {
          const cf = t.carry_forward_rule || (t.carry_forward_max ? "capped" : "none");
          const cfLabel = cf === "unlimited" ? "Unlimited" : cf === "capped" ? `Cap ${t.carry_forward_max}d` : "None";
          return (
            <div key={t.id}
                 className={`grid grid-cols-[24px_1fr_90px_120px_140px_120px_80px_120px] gap-2 px-2 py-1.5 items-center text-xs border-t border-border ${t.enabled ? "" : "opacity-50 bg-muted/20"}`}
                 data-testid={`leave-type-row-${t.id}`}>
              <span className="w-3 h-3 rounded-full" style={{ background: t.color || "#64748b" }} />
              <div className="font-medium truncate" title={t.name}>{t.name}</div>
              <code className="text-[11px]">{t.code}</code>
              <div className="font-mono">{t.annual_quota}d</div>
              <div className="capitalize">{t.accrual}</div>
              <div>{cfLabel}</div>
              <div>{t.is_paid ? <Badge variant="outline" className="rounded-none text-[9px] border-emerald-400 text-emerald-700">Paid</Badge> : <Badge variant="outline" className="rounded-none text-[9px] border-orange-400 text-orange-700">Unpaid</Badge>}</div>
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => toggleEnabled(t)} className="text-[10px] px-1.5 py-0.5 border border-border hover:border-primary" data-testid={`leave-type-toggle-${t.id}`}>
                  {t.enabled ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => setEditing(t)} className="p-1 hover:text-primary" title="Edit" data-testid={`leave-type-edit-${t.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => delType(t)} className="p-1 text-red-500 hover:text-red-600" title="Delete" data-testid={`leave-type-del-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {editing && (
        <LeaveTypeDialog
          wsId={wsId}
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }}
        />
      )}
    </div>
  );
}

function LeaveTypeDialog({ wsId, doc, onClose, onSaved }) {
  const [form, setForm] = useState(doc);
  const [busy, setBusy] = useState(false);
  const isNew = !!form._new;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (!form.name?.trim() || !form.code?.trim()) {
      toast.error("Name and code are required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      annual_quota: Number(form.annual_quota) || 0,
      accrual: form.accrual,
      carry_forward_rule: form.carry_forward_rule,
      carry_forward_max: form.carry_forward_rule === "capped" ? (Number(form.carry_forward_max) || 0) : 0,
      min_notice_days: Number(form.min_notice_days) || 0,
      requires_approval: !!form.requires_approval,
      is_paid: !!form.is_paid,
      color: form.color || null,
      enabled: !!form.enabled,
    };
    setBusy(true);
    try {
      if (isNew) {
        await api.post(`/workspaces/${wsId}/leave-types`, payload);
        toast.success("Leave type created");
      } else {
        await api.patch(`/leave-types/${form.id}`, payload);
        toast.success("Leave type updated");
      }
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="leave-type-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New leave type" : `Edit ${form.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Name</label>
              <Input value={form.name || ""} onChange={(e) => set({ name: e.target.value })}
                     placeholder="Casual Leave" className="h-9 rounded-none text-xs" data-testid="leave-type-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Code</label>
              <Input value={form.code || ""} onChange={(e) => set({ code: e.target.value.toUpperCase() })}
                     placeholder="CL" className="h-9 rounded-none text-xs font-mono" maxLength={12} data-testid="leave-type-code" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Annual quota (days)</label>
              <Input type="number" min={0} max={365} value={form.annual_quota ?? 0} onChange={(e) => set({ annual_quota: e.target.value })}
                     className="h-9 rounded-none text-xs font-mono" data-testid="leave-type-quota" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Accrual</label>
              <Select value={form.accrual} onValueChange={(v) => set({ accrual: v })}>
                <SelectTrigger className="h-9 rounded-none text-xs" data-testid="leave-type-accrual"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="annual">Annual (all at year start)</SelectItem>
                  <SelectItem value="monthly">Monthly (1/12 per month)</SelectItem>
                  <SelectItem value="quarterly">Quarterly (1/4 per quarter)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Min notice (days)</label>
              <Input type="number" min={0} max={365} value={form.min_notice_days ?? 0} onChange={(e) => set({ min_notice_days: e.target.value })}
                     className="h-9 rounded-none text-xs font-mono" data-testid="leave-type-notice" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Carry-forward rule</label>
              <Select value={form.carry_forward_rule} onValueChange={(v) => set({ carry_forward_rule: v })}>
                <SelectTrigger className="h-9 rounded-none text-xs" data-testid="leave-type-cf-rule"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (lapse at year-end)</SelectItem>
                  <SelectItem value="capped">Capped (max days below)</SelectItem>
                  <SelectItem value="unlimited">Unlimited</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Max carry-over (days) {form.carry_forward_rule !== "capped" && <span className="text-muted-foreground/60 normal-case">— not applicable</span>}
              </label>
              <Input type="number" min={0} max={365}
                     value={form.carry_forward_max ?? 0}
                     onChange={(e) => set({ carry_forward_max: e.target.value })}
                     disabled={form.carry_forward_rule !== "capped"}
                     className="h-9 rounded-none text-xs font-mono"
                     data-testid="leave-type-cf-max" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Colour</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.color || "#0ea5e9"} onChange={(e) => set({ color: e.target.value })}
                       className="h-9 w-9 border border-border rounded-none cursor-pointer" data-testid="leave-type-color" />
                <Input value={form.color || ""} onChange={(e) => set({ color: e.target.value })}
                       className="h-9 rounded-none text-xs font-mono" placeholder="#0ea5e9" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={!!form.is_paid} onCheckedChange={(v) => set({ is_paid: v })} data-testid="leave-type-paid" />
              <span><strong>Paid leave</strong><div className="text-[10px] text-muted-foreground">Deducts salary when off</div></span>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={!!form.requires_approval} onCheckedChange={(v) => set({ requires_approval: v })} data-testid="leave-type-req-approval" />
              <span><strong>Requires approval</strong><div className="text-[10px] text-muted-foreground">HR reviews requests</div></span>
            </label>
          </div>
          {!isNew && (
            <label className="flex items-center gap-2 text-xs border border-border p-2 bg-muted/20">
              <Switch checked={!!form.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="leave-type-enabled" />
              <span><strong>Enabled</strong><div className="text-[10px] text-muted-foreground">Disable to hide from new leave requests while preserving history.</div></span>
            </label>
          )}
          <AccrualPreviewTable form={form} />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="leave-type-save">
            {busy ? "Saving…" : isNew ? "Create" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * R91 · Live 12-month accrual preview.
 * Recomputes on every keystroke so admins can spot-check the formula before
 * saving. Mirrors the backend `_accrued()` logic: annual = full quota at
 * year-start, monthly = q/12 per month cumulative, quarterly = q/4 per quarter.
 * Carry-over row shows what carries into next year given the current rule.
 */
function AccrualPreviewTable({ form }) {
  const quota = Number(form.annual_quota) || 0;
  const accrual = form.accrual || "annual";
  const cfRule = form.carry_forward_rule || "none";
  const cfMax = Number(form.carry_forward_max) || 0;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const perMonth = months.map((_, i) => {
    if (accrual === "annual") return quota;
    if (accrual === "monthly") return Math.round((quota / 12) * (i + 1) * 100) / 100;
    // quarterly
    const q = Math.floor(i / 3) + 1;
    return Math.round((quota / 4) * q * 100) / 100;
  });
  const yearEnd = perMonth[11] || 0;
  const unused = Math.max(0, yearEnd);   // shown as what could carry
  const carried = cfRule === "unlimited" ? unused : cfRule === "capped" ? Math.min(unused, cfMax) : 0;
  const lapsed = Math.max(0, unused - carried);
  return (
    <div className="border border-border bg-muted/20 p-2" data-testid="accrual-preview">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Accrual preview · 12 months</div>
      <div className="grid grid-cols-12 gap-0.5 text-center text-[10px]">
        {months.map((m, i) => (
          <div key={m} className="border border-border bg-background px-1 py-0.5" data-testid={`accrual-preview-${m.toLowerCase()}`}>
            <div className="text-muted-foreground">{m}</div>
            <div className="font-mono font-semibold text-[11px]">{perMonth[i]}</div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1.5 flex flex-wrap gap-3">
        <span data-testid="accrual-preview-yearend"><strong>Year-end available:</strong> <span className="font-mono">{yearEnd}d</span></span>
        {cfRule === "capped" && <span data-testid="accrual-preview-carried"><strong>Carries next year:</strong> <span className="font-mono">{carried}d</span> (cap {cfMax}d)</span>}
        {cfRule === "unlimited" && <span data-testid="accrual-preview-carried"><strong>Carries next year:</strong> <span className="font-mono">{carried}d</span> (unlimited)</span>}
        {cfRule === "none" && lapsed > 0 && <span className="text-amber-700" data-testid="accrual-preview-lapse"><strong>Lapses at year-end:</strong> <span className="font-mono">{lapsed}d</span></span>}
      </div>
    </div>
  );
}



function ScorecardStat({ label, value, color = "", testid }) {
  return (
    <div className="border border-border bg-background p-2" data-testid={testid}>
      <div className="label-eyebrow text-[9px] text-foreground/60">{label}</div>
      <div className={`text-xl font-bold mt-0.5 font-mono ${color}`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function humanizeHours(h) {
  if (h == null || h <= 0) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h}h`;
  const days = h / 24;
  if (days < 7) return `${days.toFixed(1)}d`;
  return `${(days / 7).toFixed(1)}w`;
}


/* ─── Feb 2026 · HR bulk imports tab ─────────────────────────────────
   One form for Leaves, WFH, and Attendance CSV/Excel uploads. Preview
   surfaces every row's fate (create / update / error) BEFORE anything
   hits the DB. Also hosts the WFH "wipe imported rows" tool so admins
   can fix + reupload without leaving the tab. */

const IMPORT_KIND_META = {
  leave: {
    label: "Leaves", hint: "Columns: Employee ID (or email), Start Date, End Date (optional), Type, Status, Reason. Type accepts the leave name, code, or 'Name-Code' (e.g. 'Sick Leave', 'SL', 'Sick Leave-SL'). Writing 'half day' in Reason marks the row as a 0.5-day leave.",
    template: "Employee ID,Email,Start Date,End Date,Type,Status,Reason\nEMP001,john@acme.com,2026-02-15,2026-02-17,Casual Leave-CL,approved,Family event\nEMP001,john@acme.com,2026-02-20,2026-02-20,Sick Leave-SL,approved,half day\n",
    filename: "leaves-template.csv",
  },
  wfh: {
    label: "WFH", hint: "Columns: Employee ID (or email), Date, Status, Reason. Status accepts approved / pending / rejected.",
    template: "Employee ID,Email,Date,Status,Reason\nEMP001,john@acme.com,2026-02-20,approved,Traffic\n",
    filename: "wfh-template.csv",
  },
  attendance: {
    label: "Attendance", hint: "Columns: Employee ID (or email), Date, Check In, Check Out, Hours",
    template: "Employee ID,Email,Date,Check In,Check Out,Hours\nEMP001,john@acme.com,2026-02-19,09:12,18:30,8.5\n",
    filename: "attendance-template.csv",
  },
};


export function HrImportsTab({ wsId }) {
  const [kind, setKind] = useState("wfh");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);   // {token, stats, plan, ...}
  const [busy, setBusy] = useState(false);
  // Feb 2026 · Preview filter — quickly jump to the error rows on
  // 500-row uploads. Search matches employee name OR row number OR
  // any error text; action filter narrows to create / update / error.
  const [query, setQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  // Feb 2026 · Row-fix dialog state — click "Fix" on an error row to
  // patch its fields and re-validate that single row on the server
  // without re-uploading the entire file.
  const [fixing, setFixing] = useState(null);

  const revalidateRow = async (row, patch) => {
    if (!preview?.token) return;
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/hr/imports/re-validate-row`,
        { token: preview.token, row: row.row, patch });
      setPreview((p) => {
        if (!p) return p;
        const nextPlan = p.plan.map((r) => r.row === row.row ? data.row : r);
        return { ...p, plan: nextPlan, stats: data.stats };
      });
      setFixing(null);
      toast.success(data.row.action === "error"
        ? `Still ${data.row.errors.join(" · ")}`
        : `Row ${data.row.row} → ${data.row.action}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Fix failed"));
    }
  };

  const meta = IMPORT_KIND_META[kind];

  const upload = async () => {
    if (!file) { toast.error("Choose a file to upload"); return; }
    setBusy(true); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await api.post(
        `/workspaces/${wsId}/hr/imports/preview`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Parsed ${data.stats.total} rows`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview?.token) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/hr/imports/commit`,
        { token: preview.token, skip_errors: true });
      toast.success(`Import done — created ${data.stats.created}, updated ${data.stats.updated}, skipped ${data.stats.skipped}`);
      setPreview(null); setFile(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Commit failed"));
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([meta.template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = meta.filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  return (
    <div className="space-y-6" data-testid="hr-imports-tab">
      <div className="border border-border p-5 bg-background">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-3">
          Bulk import — CSV or Excel
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">What are you importing?</Label>
            <Select value={kind} onValueChange={(v) => { setKind(v); setPreview(null); }}>
              <SelectTrigger className="h-9 w-40 rounded-none mt-1" data-testid="hr-import-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(IMPORT_KIND_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">File (.csv, .xlsx)</Label>
            <Input type="file" accept=".csv,.xlsx,.xls"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                    className="h-9 rounded-none mt-1 max-w-[280px]"
                    data-testid="hr-import-file" />
          </div>
          <Button onClick={upload} disabled={busy || !file}
                   className="rounded-none h-9"
                   data-testid="hr-import-preview-btn">
            {busy && !preview ? "Parsing…" : "Preview →"}
          </Button>
          <Button variant="outline" onClick={downloadTemplate}
                   className="rounded-none h-9 text-xs"
                   data-testid="hr-import-template-btn">
            Download template
          </Button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          {meta.hint} · Dates as <code>YYYY-MM-DD</code> or <code>DD/MM/YYYY</code>. Re-uploading the same date+employee overwrites the previous row.
        </div>
      </div>

      {preview && (
        <div className="border border-border p-5 bg-background" data-testid="hr-import-preview">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
              Preview · {preview.kind}
            </div>
            <Button onClick={commit} disabled={busy}
                     className="rounded-none h-9"
                     data-testid="hr-import-commit-btn">
              {busy ? "Importing…" : "Confirm & import"}
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <ImportStat label="Total" value={preview.stats.total} />
            <ImportStat label="Create" value={preview.stats.create} color="text-emerald-700" />
            <ImportStat label="Update" value={preview.stats.update} color="text-amber-700" />
            <ImportStat label="Error" value={preview.stats.error} color="text-rose-700" />
          </div>
          {/* Feb 2026 · Filter bar for the preview table. Handy on
              500-row imports where errors hide in the middle of a
              long scroll. */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search rows (employee, error, row #)…"
                    className="rounded-none h-8 text-xs flex-1 min-w-[220px] max-w-[380px]"
                    data-testid="hr-import-preview-search" />
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="h-8 w-40 rounded-none text-xs"
                              data-testid="hr-import-preview-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rows</SelectItem>
                <SelectItem value="create">Only creates</SelectItem>
                <SelectItem value="update">Only updates</SelectItem>
                <SelectItem value="error">Only errors</SelectItem>
              </SelectContent>
            </Select>
            {(query || actionFilter !== "all") && (
              <button type="button" onClick={() => { setQuery(""); setActionFilter("all"); }}
                       className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground hover:text-foreground">
                Clear
              </button>
            )}
          </div>
          {(() => {
            const q = query.trim().toLowerCase();
            const filtered = preview.plan.filter((p) => {
              if (actionFilter !== "all" && p.action !== actionFilter) return false;
              if (!q) return true;
              const hay = [
                String(p.row),
                p.resolved?.user_name || "",
                p.resolved?.date || p.resolved?.start_date || "",
                p.resolved?.reason || "",
                (p.errors || []).join(" "),
              ].join(" ").toLowerCase();
              return hay.includes(q);
            });
            return (
          <div className="border border-border max-h-[420px] overflow-auto"
                data-testid="hr-import-preview-table">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-muted/60 font-semibold uppercase tracking-widest text-[9px]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Row</th>
                  <th className="px-2 py-1.5 text-left">Action</th>
                  <th className="px-2 py-1.5 text-left">Employee</th>
                  <th className="px-2 py-1.5 text-left">Date</th>
                  <th className="px-2 py-1.5 text-left">Details / errors</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground italic">
                    No rows match this filter.
                  </td></tr>
                )}
                {filtered.map((p) => {
                  const r = p.resolved || {};
                  const actClass = p.action === "create" ? "text-emerald-700"
                                  : p.action === "update" ? "text-amber-700"
                                  : "text-rose-700";
                  return (
                    <tr key={p.row} className="border-t border-border hover:bg-muted/30"
                         data-testid={`hr-import-row-${p.row}`}>
                      <td className="px-2 py-1 font-mono">{p.row}</td>
                      <td className={`px-2 py-1 font-semibold uppercase ${actClass}`}>{p.action}</td>
                      <td className="px-2 py-1">{r.user_name || <em className="text-muted-foreground">unknown</em>}</td>
                      <td className="px-2 py-1 font-mono">{r.date || r.start_date || "—"}</td>
                      <td className="px-2 py-1 text-muted-foreground">
                        {p.errors?.length ? (
                          <div className="flex items-center gap-2">
                            <span className="text-rose-700 flex items-center gap-1 flex-1">
                              <AlertCircle className="w-3 h-3" /> {p.errors.join(" · ")}
                            </span>
                            <button type="button" onClick={() => setFixing(p)}
                                     className="text-[10px] uppercase tracking-widest font-semibold text-primary hover:underline"
                                     data-testid={`hr-import-row-fix-${p.row}`}>
                              Fix
                            </button>
                          </div>
                        ) : (
                          <span>{r.status || ""} {r.reason ? `· ${r.reason}` : ""}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
            );
          })()}
          {preview.plan_truncated && (
            <div className="text-[11px] text-muted-foreground mt-2">
              Showing first 500 rows of the plan. All rows will be applied on confirm.
            </div>
          )}
        </div>
      )}

      {/* WFH bulk-delete panel — for undoing a bad import. */}
      <WfhBulkDeletePanel wsId={wsId} />

      {/* Feb 2026 · Row-fix dialog. Field set is a superset — we let
          the server ignore what doesn't apply to the current kind. */}
      {fixing && (
        <HrImportRowFixDialog
          row={fixing}
          kind={kind}
          onClose={() => setFixing(null)}
          onSave={(patch) => revalidateRow(fixing, patch)}
        />
      )}
    </div>
  );
}


function HrImportRowFixDialog({ row, kind, onClose, onSave }) {
  const r = row.resolved || {};
  const [email, setEmail] = useState("");
  const [date, setDate] = useState(r.date || r.start_date || "");
  const [endDate, setEndDate] = useState(r.end_date || "");
  const [type, setType] = useState(r.type || "");
  const [status, setStatus] = useState(r.status || "approved");
  const [reason, setReason] = useState(r.reason || "");
  const [checkIn, setCheckIn] = useState(r.check_in || "");
  const [checkOut, setCheckOut] = useState(r.check_out || "");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="hr-import-row-fix-dialog">
        <DialogHeader>
          <DialogTitle>Fix row {row.row}</DialogTitle>
          <DialogDescription>
            Correct the fields below and click <b>Re-check</b>. Only send what you want to change; blank fields keep their current value.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(row.errors || []).length > 0 && (
            <div className="border border-rose-300 bg-rose-50 text-rose-900 text-xs p-2">
              <b>Errors:</b> {row.errors.join(" · ")}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email (employee)</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="e.g. jane@acme.com"
                      className="h-8 rounded-none mt-1"
                      data-testid="hr-fix-email" />
            </div>
            <div>
              <Label className="text-xs">{kind === "leave" ? "Start date" : "Date"}</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                      className="h-8 rounded-none mt-1"
                      data-testid="hr-fix-date" />
            </div>
            {kind === "leave" && (
              <>
                <div>
                  <Label className="text-xs">End date</Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                          className="h-8 rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Leave type</Label>
                  <Input value={type} onChange={(e) => setType(e.target.value)}
                          placeholder="casual · sick · earned"
                          className="h-8 rounded-none mt-1" />
                </div>
              </>
            )}
            {kind === "attendance" && (
              <>
                <div>
                  <Label className="text-xs">Check-in (HH:mm)</Label>
                  <Input value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
                          placeholder="09:12"
                          className="h-8 rounded-none mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Check-out (HH:mm)</Label>
                  <Input value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
                          placeholder="18:30"
                          className="h-8 rounded-none mt-1" />
                </div>
              </>
            )}
            {kind !== "attendance" && (
              <div>
                <Label className="text-xs">Status</Label>
                <Input value={status} onChange={(e) => setStatus(e.target.value)}
                        placeholder="approved · pending"
                        className="h-8 rounded-none mt-1" />
              </div>
            )}
            <div className="col-span-2">
              <Label className="text-xs">Reason / notes</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                      className="h-8 rounded-none mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={() => {
              // Send only fields the admin actually touched — empty
              // string means "keep existing" here, matching how the
              // server treats missing keys.
              const patch = {};
              if (email.trim()) patch.email = email.trim();
              if (date) patch.date = date;
              if (endDate) patch.end_date = endDate;
              if (type.trim()) patch.type = type.trim();
              if (status.trim()) patch.status = status.trim();
              if (reason !== (r.reason || "")) patch.reason = reason;
              if (checkIn.trim()) patch.check_in = checkIn.trim();
              if (checkOut.trim()) patch.check_out = checkOut.trim();
              onSave(patch);
            }}
            className="rounded-none"
            data-testid="hr-fix-recheck-btn">
            Re-check row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ImportStat({ label, value, color = "" }) {
  return (
    <div className="border border-border p-3 text-center bg-muted/20">
      <div className="text-[9px] uppercase tracking-widest font-semibold text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${color}`}>{value}</div>
    </div>
  );
}


function WfhBulkDeletePanel({ wsId }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [importedOnly, setImportedOnly] = useState(true);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!from && !to) { toast.error("Set a date range"); return; }
    const suffix = importedOnly ? "imported rows" : "ALL WFH rows (including user requests)";
    if (!window.confirm(`Delete ${suffix} between ${from || "start"} and ${to || "end"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/wfh/bulk-delete`,
        { from_date: from || null, to_date: to || null,
          imported_only: importedOnly });
      toast.success(`Deleted ${data.deleted} row(s)`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-rose-300 bg-rose-50 p-5" data-testid="wfh-bulk-delete-panel">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-rose-800 mb-2 flex items-center gap-2">
        <Trash2 className="w-3.5 h-3.5" /> Bulk delete WFH rows
      </div>
      <div className="text-[11px] text-rose-800/80 mb-3">
        Undo a bad import: wipe the range, fix your sheet, re-upload. Rows created by real user requests stay untouched when "imported rows only" is on.
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                  className="h-9 rounded-none mt-1 w-40" data-testid="wfh-bd-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                  className="h-9 rounded-none mt-1 w-40" data-testid="wfh-bd-to" />
        </div>
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={importedOnly}
                  onChange={(e) => setImportedOnly(e.target.checked)}
                  data-testid="wfh-bd-imported-only" />
          Imported rows only
        </label>
        <Button onClick={run} disabled={busy}
                 variant="destructive" className="rounded-none h-9"
                 data-testid="wfh-bd-run">
          {busy ? "Deleting…" : "Delete range"}
        </Button>
      </div>
    </div>
  );
}

